import { FC, useMemo, useState } from 'react';

import { formatPrice } from '@shared/utils/formatPrice';
import { Link } from 'react-router-dom';

import { formatRate, funeralHomeNameOf } from './adminBugoStat.util';
import { OrderStatKind } from './adminOrderStat.types';
import { orderItemNameOf } from './adminOrderStat.util';
import { PenetrationFeventRow } from './adminPenetration.types';

//* 침투율 표/추이에서 "이 칸의 상가들이 뭐였지"를 열어보는 드릴다운.
//* 구매한 곳뿐 아니라 **안 산 곳**을 보는 게 이 화면의 본체다 — 놓친 상가 목록이
//* 곧 영업 대상이라서, 기본 필터에 '미구매'를 나란히 둔다.

type BuyingFilter = 'all' | 'buying' | 'idle';

const FILTERS: { value: BuyingFilter; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'buying', label: '구매' },
  { value: 'idle', label: '미구매' },
];

export const AdminPenetrationFeventModal: FC<{
  kind: OrderStatKind;
  title: string;
  subtitle?: string;
  rows: PenetrationFeventRow[];
  onClose: () => void;
}> = function AdminPenetrationFeventModal({ kind, title, subtitle, rows, onClose }) {
  const [filter, setFilter] = useState<BuyingFilter>('all');

  const counts = useMemo(() => {
    const buying = rows.filter((row) => row.orderCount > 0).length;
    return { all: rows.length, buying, idle: rows.length - buying };
  }, [rows]);

  const visible = useMemo(() => {
    if (filter === 'buying') return rows.filter((row) => row.orderCount > 0);
    if (filter === 'idle') return rows.filter((row) => row.orderCount === 0);
    return rows;
  }, [rows, filter]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-3">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative my-3 flex max-h-[92vh] w-full max-w-[900px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="border-b border-gray-200 px-4 py-3">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <p className="truncate text-18 font-bold text-gray-900">{title}</p>
              <p className="mt-0.5 text-13 tabular-nums text-gray-600">
                {`${subtitle ? `${subtitle} · ` : ''}발인 ${counts.all.toLocaleString()}상가 · 구매 ${counts.buying.toLocaleString()}상가 (${formatRate(
                  counts.buying,
                  counts.all,
                )})`}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="닫기"
              className="ml-3 shrink-0 rounded-lg px-2.5 py-1 text-20 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {FILTERS.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFilter(item.value)}
                className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
                  filter === item.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {item.label}
                <span className="ml-1 tabular-nums opacity-70">
                  {counts[item.value].toLocaleString()}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-gray-50 p-3">
          {visible.length === 0 ? (
            <p className="py-10 text-center text-14 text-gray-500">
              해당 조건의 상가가 없습니다.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {visible.map((row) => {
                const deceased = row.fevent.deceasedInfo?.name ?? '';
                const funeralHome = funeralHomeNameOf(row.fevent);
                const itemNames = row.orders
                  .map((order) => orderItemNameOf(order, kind))
                  .filter(Boolean);
                return (
                  <li
                    key={row.fevent._id}
                    className="rounded-lg bg-white px-3 py-2 shadow-smd"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-16 font-semibold text-gray-900">
                          <span className="tabular-nums text-gray-500">
                            {row.coffinOutDate ?? '발인일 없음'}
                          </span>
                          {deceased && <span className="ml-2">故 {deceased}</span>}
                          {row.orderCount === 0 && (
                            <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-12 font-medium text-gray-500">
                              미구매
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 truncate text-13 text-gray-600">
                          {row.brandName}
                          {funeralHome ? ` · ${funeralHome}` : ''}
                          {row.fevent.roomInfoEmbed?.name
                            ? ` ${row.fevent.roomInfoEmbed.name}`
                            : ''}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        {row.orderCount > 0 && (
                          <p className="text-16 font-bold tabular-nums text-indigo-700">
                            {formatPrice(Math.round(row.amount))}원
                          </p>
                        )}
                        <Link
                          to={`/fevent/${row.fevent._id}/admin-info`}
                          className="mt-0.5 inline-block rounded-lg bg-gray-100 px-3 py-1.5 text-13 font-medium text-gray-700 hover:bg-gray-200"
                        >
                          상가
                        </Link>
                      </div>
                    </div>

                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-13 tabular-nums text-gray-500">
                      <span>공유 {row.shareCount.toLocaleString()}</span>
                      <span>주문 {row.orderCount.toLocaleString()}건</span>
                      {row.quantity > 0 && <span>수량 {row.quantity.toLocaleString()}</span>}
                      {itemNames.length > 0 && (
                        <span className="min-w-0 truncate">{itemNames.join(', ')}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
